import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createShippingCost,
    deleteShippingCost,
    getShippingCosts,
    updateShippingCost,
} from "../../../api";
import { logError } from "../../../logging/loggingService";
import {
    emptyShippingCost,
    getShippingCostGroupKey,
    toNumber,
} from "./shippingCostUtils";

export const useShippingCostManagement = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [shippingCostList, setShippingCostList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentShippingCost, setCurrentShippingCost] = useState(emptyShippingCost());
    const [editingIndex, setEditingIndex] = useState(null);
    const [deleteAsk, setDeleteAsk] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("name-asc");

    const fetchShippingCosts = useCallback(() => {
        setLoading(true);
        setError(null);
        getShippingCosts()
            .then((res) => setShippingCostList(res.data || []))
            .catch((err) => {
                logError("Fehler beim Laden der Versandkosten", err, { component: "ShippingCostManagement" });
                setError("Versandkosten konnten nicht geladen werden.");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchShippingCosts();
    }, [fetchShippingCosts]);

    const net = useMemo(() => toNumber(currentShippingCost.price), [currentShippingCost.price]);
    const rate = useMemo(() => toNumber(currentShippingCost.taxRate), [currentShippingCost.taxRate]);
    const gross = useMemo(() => net * (1 + rate / 100), [net, rate]);

    const filteredSorted = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = shippingCostList;
        if (q) {
            list = list.filter((cost) => (cost.name || "").toLowerCase().includes(q));
        }
        const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "de");
        const byPrice = (a, b) => toNumber(a.price) - toNumber(b.price);

        switch (sortBy) {
            case "name-desc":
                return [...list].sort((a, b) => -byName(a, b));
            case "price-asc":
                return [...list].sort(byPrice);
            case "price-desc":
                return [...list].sort((a, b) => -byPrice(a, b));
            case "name-asc":
            default:
                return [...list].sort(byName);
        }
    }, [shippingCostList, query, sortBy]);

    const shippingCostIndexById = useMemo(() => {
        const map = new Map();
        shippingCostList.forEach((item, index) => {
            if (item?.id) {
                map.set(item.id, index);
            }
        });
        return map;
    }, [shippingCostList]);

    const groupedShippingCosts = useMemo(() => {
        const groups = new Map();
        filteredSorted.forEach((shippingCost) => {
            const group = getShippingCostGroupKey(shippingCost);
            if (!groups.has(group)) {
                groups.set(group, []);
            }
            groups.get(group).push(shippingCost);
        });

        const keys = Array.from(groups.keys()).sort((a, b) => {
            if (a === "#") return 1;
            if (b === "#") return -1;
            return a.localeCompare(b, "de");
        });

        return keys.map((key) => ({
            key,
            shippingCosts: groups.get(key),
        }));
    }, [filteredSorted]);

    const resolveShippingCostIndex = (shippingCost) => {
        if (shippingCost?.id) {
            const index = shippingCostIndexById.get(shippingCost.id);
            if (index !== undefined) {
                return index;
            }
            return shippingCostList.findIndex((item) => item?.id === shippingCost.id);
        }
        return shippingCostList.findIndex((item) => item === shippingCost);
    };

    const handleDialogOpen = () => {
        setCurrentShippingCost(emptyShippingCost());
        setEditingIndex(null);
        setFieldErrors({});
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setCurrentShippingCost(emptyShippingCost());
        setEditingIndex(null);
        setFieldErrors({});
    };

    const handleEdit = (shippingCost) => {
        const index = resolveShippingCostIndex(shippingCost);
        if (index < 0) return;
        setCurrentShippingCost(shippingCostList[index]);
        setEditingIndex(index);
        setFieldErrors({});
        setOpenDialog(true);
    };

    const handleDeleteAsk = (shippingCost) => {
        const index = resolveShippingCostIndex(shippingCost);
        if (index < 0) return;
        setDeleteAsk({ shippingCost, index });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteAsk) return;
        const prev = shippingCostList;
        setShippingCostList((p) => p.filter((_, i) => i !== deleteAsk.index));
        try {
            await deleteShippingCost(deleteAsk.shippingCost.id);
            setToast({ open: true, msg: "Versandkosten gelöscht.", severity: "success" });
        } catch (err) {
            const msg = err?.response?.data || "Löschen fehlgeschlagen.";
            logError("Fehler beim Löschen der Versandkosten", err, { component: "ShippingCostManagement" });
            setToast({ open: true, msg, severity: "error" });
            setShippingCostList(prev);
        } finally {
            setDeleteAsk(null);
        }
    };

    const handleFieldChange = (field, value) => {
        setCurrentShippingCost((prev) => ({ ...prev, [field]: value }));
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleSave = async () => {
        if (!currentShippingCost.name?.trim()) {
            setFieldErrors({ name: "Bitte Bezeichnung angeben." });
            setToast({ open: true, msg: "Bitte prüfe die markierten Felder.", severity: "warning" });
            return;
        }
        const payload = {
            ...currentShippingCost,
            price: toNumber(currentShippingCost.price),
            taxRate: currentShippingCost.taxRate === "" ? null : toNumber(currentShippingCost.taxRate),
        };

        try {
            if (editingIndex !== null) {
                const res = await updateShippingCost(payload.id, payload);
                setShippingCostList((list) => {
                    const next = [...list];
                    next[editingIndex] = res.data;
                    return next;
                });
                setToast({ open: true, msg: "Versandkosten aktualisiert.", severity: "success" });
            } else {
                const res = await createShippingCost(payload);
                setShippingCostList((list) => [...list, res.data]);
                setToast({ open: true, msg: "Versandkosten angelegt.", severity: "success" });
            }
            handleDialogClose();
        } catch (err) {
            logError("Fehler beim Speichern der Versandkosten", err, { component: "ShippingCostManagement" });
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const field = rawField.split(".").pop();
                    nextErrors[field] = message;
                });
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    setToast({
                        open: true,
                        msg: backendError?.message || "Bitte prüfe die markierten Felder.",
                        severity: "error",
                    });
                    return;
                }
            }
            setToast({ open: true, msg: "Speichern fehlgeschlagen.", severity: "error" });
        }
    };

    const handleRefresh = () => {
        fetchShippingCosts();
    };

    const closeToast = () => setToast((prev) => ({ ...prev, open: false }));

    return {
        openDialog,
        shippingCostList,
        loading,
        error,
        currentShippingCost,
        editingIndex,
        deleteAsk,
        fieldErrors,
        toast,
        query,
        sortBy,
        net,
        rate,
        gross,
        filteredSorted,
        groupedShippingCosts,
        setError,
        setQuery,
        setSortBy,
        setCurrentShippingCost,
        setDeleteAsk,
        handleDialogOpen,
        handleDialogClose,
        handleEdit,
        handleDeleteAsk,
        handleDeleteConfirm,
        handleFieldChange,
        handleSave,
        handleRefresh,
        closeToast,
    };
};
